import {
  ColumnDef,
  RowData,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { memo, useEffect } from 'react'
import { twMerge } from 'tailwind-merge'
import { Th } from '../Th'
import { Td } from '../Td'
import { Pagination } from '../pagination'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string
  }
}

const SimpleTableComponent = <T,>({
  className,
  columns,
  data,
  defaultSortColumn,
  pageSize,
}: {
  className?: string
  columns: ColumnDef<T, unknown>[]
  data: T[]
  defaultSortColumn?: string
  pageSize?: number
}) => {
  const initialState = {
    ...(defaultSortColumn && {
      sorting: [{ id: defaultSortColumn, desc: false }],
    }),
    ...(pageSize && { pagination: { pageIndex: 0, pageSize } }),
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pageSize && { getPaginationRowModel: getPaginationRowModel() }),
    autoResetPageIndex: false,
    initialState,
  })

  const pageCount = table.getPageCount()
  const { pageIndex } = table.getState().pagination

  useEffect(() => {
    if (pageSize && pageCount > 0 && pageIndex >= pageCount)
      table.setPageIndex(pageCount - 1)
  }, [pageSize, pageCount, pageIndex, table])

  return (
    <div
      className={twMerge(
        'rounded-lg border border-neutral-300 w-full',
        className,
      )}
    >
      <div className="overflow-x-auto rounded-lg">
        <table className="text-sm w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    className={twMerge(
                      'border-none',
                      header.column.columnDef.meta?.className,
                    )}
                    key={header.id}
                    label={
                      header.isPlaceholder
                        ? ''
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                    }
                    clickable={header.column.getCanSort()}
                    onClick={header.column.getToggleSortingHandler()}
                    sortDir={
                      header.column.getIsSorted() === 'asc'
                        ? 'asc'
                        : header.column.getIsSorted() === 'desc'
                        ? 'desc'
                        : false
                    }
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="group">
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    className={twMerge(
                      'px-4 py-2 border-x-0 group-last:border-b-0',
                      className,
                      cell.column.columnDef.meta?.className,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pageSize !== undefined && data.length > pageSize && (
        <Pagination
          className="border-b-0"
          onPageNumberChange={(pageNumber) =>
            table.setPageIndex(pageNumber - 1)
          }
          onPageSizeChange={(size) => table.setPageSize(size)}
          pageNumber={pageIndex + 1}
          pageSize={table.getState().pagination.pageSize}
          pending={false}
          totalPages={pageCount}
          totalRows={table.getPrePaginationRowModel().rows.length}
        />
      )}
    </div>
  )
}

export const SimpleTable = memo(
  SimpleTableComponent,
) as typeof SimpleTableComponent
